import { useRef, useEffect, useCallback } from 'react'
import { formatCurrency, booleanStringIsTruthy } from 'lib/helpers'
import { ResponsiveImage } from '../ResponsiveImage'
import { useAppStore } from 'lib/hooks/useAppStore'
import {
  useCart,
  CartCheckoutButton,
  CartLineProvider,
  CartLineQuantityAdjustButton,
} from '@shopify/hydrogen-react'

import { motion, AnimatePresence } from 'motion/react'
import { Button } from 'components/Global/Button'
import { ProductTags } from 'components/Product/ProductTags'

export const CartButton = () => {
  const { totalQuantity, lines } = useCart()
  const { cartState, setCartState } = useAppStore()
  const checkoutRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const handleScroll = () => {
      if (cartState === 'expanded') {
        setCartState('closed')
      }
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', handleScroll)
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('scroll', handleScroll)
      }
    }
  }, [cartState])

  const handleMouseLeave = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      // check if relatedTarget is not a child of the cart
      if (
        cartState !== 'open' &&
        e.relatedTarget &&
        !e.currentTarget?.contains(e.relatedTarget as Node)
      ) {
        setCartState('closed')
      }
    },
    [cartState]
  )

  return (
    <div className="relative" onMouseLeave={handleMouseLeave}>
      <div className="background-blur ml-8 rounded-8">
        <Button
          as="button"
          className="leading-[16px]"
          onMouseEnter={() =>
            setCartState(cartState === 'open' ? 'open' : 'expanded')
          }
          onClick={() => setCartState('open')}
        >
          Cart [{totalQuantity}]
        </Button>
      </div>
      <AnimatePresence>
        {cartState === 'expanded' && (
          <motion.div className="absolute right-0 top-full pt-8">
            <motion.div
              initial={{ height: '0px' }}
              animate={{ height: 'auto' }}
              exit={{ height: '0px' }}
              transition={{
                duration: 0.6,
                ease: [0.88, 0.01, 0.11, 0.98],
              }}
              className="relative overflow-hidden rounded-8"
            >
              <div className="background-blur relative max-h-[600px] w-[400px] overflow-hidden rounded-8 p-12">
                <div className="flex max-h-[460px] flex-1 flex-col justify-between gap-8 overflow-y-scroll overscroll-contain">
                  {!lines || lines.length <= 0 ? (
                    <div className="text-14">Your cart is empty</div>
                  ) : null}
                  {lines?.map((line: any) => {
                    if (!line) return null
                    const customImage = line.attributes?.find(
                      (attribute) => attribute?.key === 'Image'
                    )
                    const isPreorder = booleanStringIsTruthy(
                      line.merchandise?.product?.preorder?.value
                    )
                    const isBackorder = booleanStringIsTruthy(
                      line.merchandise?.product?.backorder?.value
                    )
                    return (
                      <CartLineProvider line={line} key={line.id}>
                        <div className="flex justify-between rounded-8 bg-white px-10 py-12">
                          <div className="flex flex-1 flex-col justify-between gap-10 pr-20 text-12 leading-100">
                            <div className="flex flex-col gap-10">
                              <ProductTags
                                isPreorder={isPreorder}
                                isBackorder={isBackorder}
                                discountAmount={
                                  line.merchandise?.product?.discountAmount
                                    ?.value
                                }
                              />
                              <div>{line.merchandise?.product?.title}</div>
                              {line.merchandise?.selectedOptions?.map(
                                (option) => {
                                  if (!option) return null
                                  return (
                                    <div key={option.name}>
                                      {option.name}: {option.value}
                                    </div>
                                  )
                                }
                              )}
                              <div>
                                {formatCurrency(line.cost?.totalAmount?.amount)}
                              </div>
                            </div>
                            <CartLineQuantityAdjustButton
                              className="text-left"
                              adjust="remove"
                            >
                              Remove
                            </CartLineQuantityAdjustButton>
                          </div>
                          <div
                            className="relative h-[100px] w-auto overflow-hidden rounded-6"
                            style={{
                              aspectRatio: `${line.merchandise?.image?.width} / ${line.merchandise?.image?.height}`,
                            }}
                          >
                            <ResponsiveImage
                              src={
                                customImage?.value ??
                                line.merchandise?.image?.url
                              }
                              alt={line.merchandise?.image?.altText ?? ''}
                              position="right"
                            />
                          </div>
                        </div>
                      </CartLineProvider>
                    )
                  })}
                </div>
                <div className="flex flex-col gap-4 pt-32 text-darkGray">
                  <CartCheckoutButton
                    buttonRef={checkoutRef}
                    disabled={totalQuantity === 0}
                  >
                    <div
                      className={`ease w-full rounded-6 bg-yellow px-12 py-12 text-center uppercase transition-colors duration-300 ${
                        totalQuantity === 0
                          ? 'cursor-not-allowed'
                          : 'cursor-pointer'
                      }`}
                    >
                      Checkout
                    </div>
                  </CartCheckoutButton>
                  <Button
                    onClick={() => setCartState('open')}
                    className="w-full bg-[#eee] text-center uppercase"
                  >
                    Go To Cart
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
